"use client";

import Script from "next/script";
import { useOptionalScriptsAllowed } from "@/lib/consent";

/* Client-side analytics pixels.
 *
 * Inject only the ones that have an env var set AND the buyer has
 * granted cookie consent. Backend handles server-side Conversions
 * API; we only fire page-view + browse events here. Purchase events
 * fire server-side from the apex-flow-labs webhook with event_id
 * dedupe — DO NOT fire purchase client-side or Meta/TikTok will
 * double-count.
 *
 * Consent gating (GDPR / CCPA):
 *   - On first paint, render nothing.
 *   - Read localStorage for `apex_cookie_consent`.
 *     - 'accepted' → render every configured pixel.
 *     - 'declined' or absent → render nothing.
 *   - Listen for an `apex:consent` window event so accept-click in
 *     the CookieBanner lights up tracking without a page reload.
 *
 * This means non-consented EU/UK/CA visitors never load fbevents.js,
 * gtag.js, ttq.js, clarity.js, or klaviyo.js. */

const GA4_ID =
  process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID ||
  process.env.NEXT_PUBLIC_GA4_ID;
const META_PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID;
const TIKTOK_PIXEL_ID = process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID;
const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;
const CLARITY_PROJECT_ID = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;
const KLAVIYO_PUBLIC_KEY = process.env.NEXT_PUBLIC_KLAVIYO_API_KEY;

export function Pixels() {
  /* Optional trackers load ONLY after an explicit Accept; Global Privacy Control counts as Decline. */
  const consented = useOptionalScriptsAllowed();

  if (!consented) return null;

  return (
    <>
      {/* GA4 + Google Ads (gtag.js deferred to idle since analytics
       * doesn't need to block the main thread during page load.
       * The inline init queues config calls in dataLayer so they
       * fire once gtag.js loads lazily.) */}
      {(GA4_ID || GOOGLE_ADS_ID) && (
        <>
          <Script id="gtag-init" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              ${GA4_ID ? `gtag('config', '${GA4_ID}', { send_page_view: true });` : ""}
              ${GOOGLE_ADS_ID ? `gtag('config', '${GOOGLE_ADS_ID}');` : ""}
            `}
          </Script>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${GA4_ID || GOOGLE_ADS_ID}`}
            strategy="lazyOnload"
          />
        </>
      )}

      {/* Meta Pixel */}
      {META_PIXEL_ID && (
        <Script id="meta-pixel" strategy="afterInteractive">
          {`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${META_PIXEL_ID}');
            fbq('track', 'PageView');
          `}
        </Script>
      )}

      {/* TikTok Pixel */}
      {TIKTOK_PIXEL_ID && (
        <Script id="tiktok-pixel" strategy="afterInteractive">
          {`
            !function (w, d, t) {
              w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
              ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],
              ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
              for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
              ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
              ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";
              ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=i;ttq._t=ttq._t||{};ttq._t[e]=+new Date;
              ttq._o=ttq._o||{};ttq._o[e]=n||{};
              var o=document.createElement("script");o.type="text/javascript";o.async=!0;
              o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];
              a.parentNode.insertBefore(o,a)};
              ttq.load('${TIKTOK_PIXEL_ID}');
              ttq.page();
            }(window, document, 'ttq');
          `}
        </Script>
      )}

      {/* Klaviyo client tracker — deferred to idle since it only
       * drops a cookie for later server-side attribution. The .js
       * payload (~35 KB) is never on the critical rendering path. */}
      {KLAVIYO_PUBLIC_KEY && (
        <Script
          id="klaviyo"
          strategy="lazyOnload"
          src={`https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${KLAVIYO_PUBLIC_KEY}`}
        />
      )}

      {/* Microsoft Clarity — deferred to idle; session replay
       * recording doesn't need to block the page from loading. */}
      {CLARITY_PROJECT_ID && (
        <Script id="clarity" strategy="lazyOnload">
          {`
            (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "${CLARITY_PROJECT_ID}");
          `}
        </Script>
      )}
    </>
  );
}
